import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { KPICard } from './KPICard';
import { VelocityData } from '../types/analytics.types';
import './VelocityMetrics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface VelocityMetricsProps {
  data: VelocityData;
  loading?: boolean;
}

export const VelocityMetrics: React.FC<VelocityMetricsProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="velocity-metrics">
        <h3>Learning Velocity</h3>
        <div className="velocity-cards">
          <KPICard title="Avg Velocity" value="0" loading={true} />
          <KPICard title="Active Learners" value="0" loading={true} />
        </div>
      </div>
    );
  }

  if (!data || !data.time_series || data.time_series.length === 0) {
    return (
      <div className="velocity-metrics">
        <h3>Learning Velocity</h3>
        <p className="no-data">No velocity data available</p>
      </div>
    );
  }

  // Prepare chart data
  const labels = data.time_series.map(item => item.week);
  const completionData = data.time_series.map(item => item.sections_completed);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Sections Completed',
        data: completionData,
        borderColor: '#9B59B6',
        backgroundColor: 'rgba(155, 89, 182, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#9B59B6',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function(context: any) {
            return `Sections Completed: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    }
  };

  // Find top performers
  const topPerformers = [...data.user_velocities]
    .sort((a, b) => b.velocity_sections_per_week - a.velocity_sections_per_week)
    .slice(0, 5);

  return (
    <div className="velocity-metrics">
      <h3>Learning Velocity</h3>

      <div className="velocity-cards">
        <KPICard
          title="Avg Velocity"
          value={`${data.avg_velocity_sections_per_week.toFixed(2)}`}
          subtitle="Sections/week"
          icon="⚡"
        />
        <KPICard
          title="Active Learners"
          value={data.total_users_analyzed.toLocaleString()}
          subtitle="With completion data"
          icon="👥"
        />
      </div>

      {data.time_series.length > 1 && (
        <div className="velocity-chart">
          <h4>Completion Trend Over Time</h4>
          <div className="chart-wrapper" style={{ height: '200px' }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>
      )}

      {topPerformers.length > 0 && (
        <div className="top-performers">
          <h4>🏆 Top Learners by Velocity</h4>
          <div className="performers-list">
            {topPerformers.map((user, index) => (
              <div key={user.user_id} className="performer-item">
                <span className="rank">#{index + 1}</span>
                <span className="user-id">{user.user_id.substring(0, 12)}...</span>
                <span className="sections">{user.sections_completed} sections</span>
                <span className="velocity">
                  {user.velocity_sections_per_week.toFixed(2)} sec/week
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VelocityMetrics;
